import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { FileText } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Terms of Service | Nix Spaces Workplace Monitoring"
        description="Terms of Service for Nix Spaces workplace phone monitoring software. Review our legal terms and conditions for using our employee monitoring platform."
        canonical="https://nixspaces.com/terms"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/pricing" className="text-gray-600 hover:text-blue-600 transition-colors">Pricing</Link>
              <Link to="/privacy" className="text-gray-600 hover:text-blue-600 transition-colors">Privacy</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="h-16 w-16 text-cyan-600 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: January 2024
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-8">
              <p className="text-gray-700 mb-0">
                These Terms of Service govern your use of the Nix Spaces workplace phone monitoring platform ("Service") provided by GoPoint ("Company", "we", "us", or "our"). By accessing or using the Service, you agree to be bound by these Terms.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Service Description</h2>
            <p className="text-gray-700 mb-4">
              Nix Spaces is a workplace phone activity monitoring solution designed for employers to track employee device usage during work hours for safety, compliance, and productivity purposes. The Service is intended for use by organizations ("Customers") to monitor employee-owned or company-owned devices with proper authorization and employee notice.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Eligibility and Account Requirements</h2>
            <p className="text-gray-700 mb-4">
              <strong>2.1 Customer Accounts:</strong> Only authorized representatives of organizations may create Customer accounts. You represent and warrant that you have the legal authority to bind your organization to these Terms.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>2.2 Employee Notice Requirement:</strong> Customers must provide clear written notice to all monitored employees that phone monitoring is in effect during work hours. Customers are solely responsible for compliance with all applicable employment, privacy, and data protection laws.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>2.3 Age Requirement:</strong> The Service is intended for workplace monitoring of adult employees. Customers must not use the Service to monitor individuals under the age of 18 without appropriate legal authorization.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Permitted Use and Restrictions</h2>
            <p className="text-gray-700 mb-4">
              <strong>3.1 Permitted Use:</strong> The Service may be used solely for legitimate workplace monitoring purposes, including safety compliance, productivity management, and regulatory documentation.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>3.2 Prohibited Use:</strong> You may NOT use the Service to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Monitor individuals without their knowledge or proper legal authorization</li>
              <li>Violate any applicable privacy, employment, or data protection laws</li>
              <li>Engage in unlawful surveillance or harassment</li>
              <li>Monitor personal devices of non-employees without consent</li>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Service</li>
              <li>Resell, redistribute, or sublicense the Service without written authorization</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Customer Responsibilities</h2>
            <p className="text-gray-700 mb-4">
              <strong>4.1 Legal Compliance:</strong> Customers are solely responsible for ensuring their use of the Service complies with all applicable federal, state, and local laws, including employment laws, privacy regulations, and collective bargaining agreements.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>4.2 Data Security:</strong> Customers must maintain appropriate security measures for their account credentials and manager access. You are responsible for all activities conducted under your account.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>4.3 Employee Communication:</strong> Customers must clearly communicate monitoring policies to employees and obtain any necessary consents or authorizations as required by law.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Data Collection and Privacy</h2>
            <p className="text-gray-700 mb-4">
              <strong>5.1 Monitoring Scope:</strong> The Service collects activity timestamps, app usage duration, and device usage metrics during configured work hours. The Service does not access message content, photos, videos, or other personal data.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>5.2 Data Ownership:</strong> Customers retain ownership of all monitoring data generated through their use of the Service. We process such data solely as a service provider on behalf of the Customer.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>5.3 Privacy Policy:</strong> Our collection and use of data is governed by our <Link to="/privacy" className="text-cyan-600 hover:text-cyan-700 font-semibold">Privacy Policy</Link>, which is incorporated into these Terms by reference.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Payment and Subscription</h2>
            <p className="text-gray-700 mb-4">
              <strong>6.1 Fees:</strong> Customers agree to pay all applicable subscription fees as specified in their service agreement or pricing plan. Fees are typically charged on a per-employee per-month basis.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>6.2 Billing:</strong> Subscription fees are billed in advance on a monthly or annual basis. Payment is due upon invoice receipt unless otherwise specified in writing.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>6.3 Late Payment:</strong> We reserve the right to suspend access to the Service for accounts with overdue payments exceeding 30 days.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Service Availability and Support</h2>
            <p className="text-gray-700 mb-4">
              <strong>7.1 Uptime:</strong> We strive to maintain high Service availability but do not guarantee uninterrupted access. Scheduled maintenance windows will be communicated in advance when possible.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>7.2 Support:</strong> Customer support is provided via email and documentation. Response times vary by subscription tier (Starter, Professional, Enterprise).
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Termination</h2>
            <p className="text-gray-700 mb-4">
              <strong>8.1 Termination by Customer:</strong> Customers may terminate their subscription at any time by providing written notice. Cancellation takes effect at the end of the current billing period. No refunds are provided for partial months.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>8.2 Termination by Company:</strong> We reserve the right to suspend or terminate accounts that violate these Terms, engage in prohibited use, or fail to make timely payments.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>8.3 Effect of Termination:</strong> Upon termination, Customer access to the Service will cease. Monitoring data may be retained for a limited period to comply with legal obligations or may be deleted at Customer request.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Disclaimers and Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              <strong>9.1 No Warranty:</strong> THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>9.2 Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE FEES PAID BY CUSTOMER DURING THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>9.3 Exclusion of Damages:</strong> WE SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR DATA LOSS.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 mb-4">
              Customer agrees to indemnify, defend, and hold harmless the Company, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to Customer's use of the Service, violation of these Terms, or violation of any applicable laws or third-party rights.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these Terms at any time. Material changes will be communicated via email or in-app notification. Continued use of the Service after changes take effect constitutes acceptance of the modified Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Governing Law and Dispute Resolution</h2>
            <p className="text-gray-700 mb-4">
              <strong>12.1 Governing Law:</strong> These Terms are governed by the laws of the State of California, without regard to conflict of law principles.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>12.2 Dispute Resolution:</strong> Any disputes arising out of or related to these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in court for intellectual property violations.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. General Provisions</h2>
            <p className="text-gray-700 mb-4">
              <strong>13.1 Entire Agreement:</strong> These Terms, together with the Privacy Policy and any written service agreements, constitute the entire agreement between you and the Company regarding the Service.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>13.2 Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>13.3 No Waiver:</strong> Failure to enforce any provision of these Terms does not constitute a waiver of that provision.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms of Service, please contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-8">
              <p className="text-gray-700 mb-2">
                <strong>GoPoint / Nix Spaces</strong><br />
                Email: <a href="mailto:support@nixspaces.com" className="text-cyan-600 hover:text-cyan-700">support@nixspaces.com</a><br />
                Website: <a href="https://nixspaces.com" className="text-cyan-600 hover:text-cyan-700">nixspaces.com</a>
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mt-8">
              <p className="text-gray-700 mb-0">
                <strong>Important:</strong> These Terms govern the relationship between the Company and Customer organizations. Customers remain solely responsible for compliance with employment, privacy, and data protection laws applicable to their use of workplace monitoring technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto filter invert" />
              <span className="text-xl font-bold">Spaces</span>
            </Link>
            
            <div className="flex flex-wrap items-center justify-center space-x-6 text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-400">
                © 2024 Nix Spaces. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
